import { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useQueryParams } from "@/hooks/use-query-param";
import { useSnackbar } from "@/hooks/useSnackbar";
import { HOME_CARE_ROS_INITIAL_DATA } from "@/pages/app/admin/library/templates/home-care-ros/home-care-ros-data";
import { useRosPeTemplateArchive } from "@/queries/encounter/use-ros-pe-template-archive";
import { useRosPeTemplateCreate } from "@/queries/encounter/use-ros-pe-template-create";
import { useRosPeTemplateList } from "@/queries/encounter/use-ros-pe-template-list";
import { useRosPeTemplateUpdate } from "@/queries/encounter/use-ros-pe-template-update";
import { PhysicalROSTemplateTemplateTypeEnum } from "@/services";
import {
  getErrorSnackbar,
  getSuccessSnackbar,
  getXtenantId,
} from "@/utils/utils";
import {
  ROS_PE_LIST_KEY,
  ROS_PE_MISSING_APPOINTMENT_MESSAGE,
  ROS_PE_PAGE_SIZE,
} from "../initial-f2f-constant";
import { TemplateTypeSet } from "../initial-f2f-types";
import {
  buildRosPeSavePlan,
  mergeSavedIntoSystems,
  toSavedRosPe,
} from "../initial-f2f-utils";

export interface UseRosPeSection {
  systems: TemplateTypeSet;
  isLoading: boolean;
  isSaving: boolean;
  save: (systems: TemplateTypeSet) => Promise<void>;
}

/** Loads the appointment's saved ROS or PE and writes edits back through the encounter-template API. */
export const useRosPeSection = (
  templateType: PhysicalROSTemplateTemplateTypeEnum
): UseRosPeSection => {
  const { patientId } = useParams() as { patientId?: string };
  const appointmentId = useQueryParams("appointmentid");
  const xTENANTID = getXtenantId();
  const queryClient = useQueryClient();
  const showSnackbar = useSnackbar();
  const [isSaving, setIsSaving] = useState(false);

  const { mutateAsync: createEntries } = useRosPeTemplateCreate();
  const { mutateAsync: updateEntries } = useRosPeTemplateUpdate();
  const { mutateAsync: archiveEntry } = useRosPeTemplateArchive();

  const { data, isLoading } = useRosPeTemplateList({
    pagination: { page: 0, pageSize: ROS_PE_PAGE_SIZE },
    gridSorting: [],
    archive: false,
    status: true,
    templateType,
    appointmentUuid: appointmentId ?? undefined,
    xTENANTID,
  });

  const saved = useMemo(
    () => toSavedRosPe(data?.data?.content),
    [data?.data?.content]
  );

  const systems = useMemo(
    () => mergeSavedIntoSystems(HOME_CARE_ROS_INITIAL_DATA, saved),
    [saved]
  );

  const save = useCallback(
    async (next: TemplateTypeSet) => {
      if (!appointmentId) {
        showSnackbar({
          type: "error",
          message: ROS_PE_MISSING_APPOINTMENT_MESSAGE,
        });
        return;
      }
      const plan = buildRosPeSavePlan(next, saved, templateType);
      const request = (list: typeof plan.create) => ({
        physicalROSTemplateRequest: {
          appointmentId,
          patientId,
          physicalROSTemplateList: list,
        },
        xTENANTID,
      });
      setIsSaving(true);
      try {
        const responses = [
          ...(plan.create.length
            ? [await createEntries(request(plan.create))]
            : []),
          ...(plan.update.length
            ? [await updateEntries(request(plan.update))]
            : []),
          ...(await Promise.all(
            plan.archiveIds.map((encounterTemplateId) =>
              archiveEntry({ encounterTemplateId, status: true, xTENANTID })
            )
          )),
        ];
        const last = responses[responses.length - 1];
        if (last) showSnackbar(getSuccessSnackbar(last));
        await queryClient.invalidateQueries({ queryKey: [ROS_PE_LIST_KEY] });
      } catch (err) {
        showSnackbar(getErrorSnackbar(err));
      } finally {
        setIsSaving(false);
      }
    },
    [
      appointmentId,
      patientId,
      saved,
      templateType,
      xTENANTID,
      createEntries,
      updateEntries,
      archiveEntry,
      queryClient,
      showSnackbar,
    ]
  );

  return useMemo(
    () => ({ systems, isLoading, isSaving, save }),
    [systems, isLoading, isSaving, save]
  );
};
